import React from 'react';
import { ReportOutputType } from '../../graphql/schema/p4types';
import { useFormContext } from 'react-hook-form';
import Dropdown from '../connectedForm/Dropdown';
import { IDocumentValues, useDocumentCarouselStyles } from './documentCarouselUtil';
import ToggleInput from '../connectedForm/ToggleInput/ToggleInput';
import getValue from '../connectedForm/utils/getValue';
import Fieldset from '../connectedForm/Fieldset';
import { Switch } from '@fluentui/react-components';
import HintIcon from '../connectedForm/HintIcon';
import { useCookies } from 'react-cookie';
import { add } from 'date-fns';
import { PRINT_INDIVIDUAL_PREFERENCE_COOKIE } from '../../views/document/AcknowledgementDocumentCarouselCard';
import FluentSwitch from '../FluentWrapper/FluentSwitch';

/**
 * Provides the "print individually" and "print contiguous" fields for document printing.
 * @param singlePrint - If printing a single document, these fields are not required and the component will return null.
 */
const DocumentCarouselPrintOptions = ({
  singlePrint,
  setCookie,
}: {
  singlePrint: boolean;
  setCookie: ReturnType<typeof useCookies>[1];
}) => {
  const { watch, setValue } = useFormContext<IDocumentValues>();
  const styles = useDocumentCarouselStyles();

  const outputType = watch('outputType');

  if (singlePrint || ![ReportOutputType.LocalPrinter, ReportOutputType.Email].includes(outputType)) {
    return null;
  }

  const printIndividually = watch('printIndividually');
  const overrideOutput = watch('overrideOutput');

  return (
    <div className={styles.row}>
      <Fieldset cols={1 / 3} style={{ paddingLeft: 0, paddingRight: 0 }}>
        <FluentSwitch
          data-testid="document-carousel-print-individually"
          className={styles.switch}
          checked={printIndividually}
          label={`${outputType === ReportOutputType.LocalPrinter ? 'Print' : 'Send'} Individually`}
          onChange={(ev, data) => {
            setValue('printIndividually', data.checked ?? false, { shouldTouch: true, shouldDirty: true });
          }}
          hint={
            <span>
              {outputType === ReportOutputType.LocalPrinter
                ? 'Documents will be generated individually and be downloaded as a ZIP file'
                : 'Documents will be generated individually and sent in individual e-mails'}
            </span>
          }
        />
      </Fieldset>
      {printIndividually && !overrideOutput ? (
        <Fieldset cols={2 / 3} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Dropdown
            data-testid="document-carousel-print-contiguous"
            options={[
              {
                value: false,
                label: 'Print / Send All Individually',
              },
              {
                value: true,
                label: 'Print Together / Send Individually',
              },
            ]}
            valueKey="value"
            labelKey="label"
            onChange={(newPrintContiguous) => {
              setValue('printContiguous', newPrintContiguous, {
                shouldTouch: true,
                shouldDirty: true,
              });
              setCookie(PRINT_INDIVIDUAL_PREFERENCE_COOKIE, newPrintContiguous, {
                expires: add(new Date(), { years: 1 }),
              });
            }}
            value={watch('printContiguous')}
            valueOnly={true}
            includeLabel={false}
            clearable={false}
            rawLabel
          />
        </Fieldset>
      ) : null}
    </div>
  );
};

export default DocumentCarouselPrintOptions;
